import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

import isValidDate from "@utils/isValidDate"

import { BULLET_CHARACTER } from "@particles/Bullet/Bullet"

dayjs.extend(advancedFormat)
dayjs.extend(timezone)
dayjs.extend(utc)

export interface DateFormatOptions {
    short?: boolean
    addDay?: boolean
    addFullDay?: boolean
    addYear?: boolean
    datetime?: string | Date
    preferredTimezone?: string
}

export default function formatDate(options: DateFormatOptions) {
    const { datetime, preferredTimezone, short = false, addDay = false, addFullDay = false, addYear = false } = options

    const localTime = dayjs(datetime).tz(preferredTimezone)

    let dateFormatString: string
    let timeFormatString: string

    if (short) {
        dateFormatString = "MMM D"
        timeFormatString = "h:mm A"
    } else {
        dateFormatString = "MMM Do"
        timeFormatString = "h:mm A"
    }

    if (addDay || addFullDay) {
        dateFormatString = `${addFullDay ? "dddd" : "ddd"}, ` + dateFormatString
    }

    if (addYear) {
        dateFormatString = dateFormatString + ", YYYY"
    }

    const localDateString = localTime.format(dateFormatString)
    const localTimeString = localTime.format(timeFormatString)

    return {
        localDateString,
        localTimeString,
        localDateTimeString: `${localDateString} ${BULLET_CHARACTER} ${localTimeString}`,
        isValidDate: isValidDate(datetime ?? null),
    }
}

export type StandardDateFormat = "short" | "long"

/**
 * Format a date the standard way.
 *
 * "short" omits the year for the current year ("Jan 1") and keeps it otherwise ("Jan 1, 2024").
 * "long" spells out the month and always keeps the year ("January 1, 2025").
 */
export function formatStandardDateString(
    date: string | Date | null | undefined,
    preferredTimezone?: string,
    format: StandardDateFormat = "short",
): string | null {
    if (!date) {
        return null
    }

    const localDate = dayjs(date).tz(preferredTimezone)

    if (format === "long") {
        return localDate.format("MMMM D, YYYY")
    } else {
        const isCurrentYear = localDate.year() === dayjs().year()
        return localDate.format(isCurrentYear ? "MMM D" : "MMM D, YYYY")
    }
}

/**
 * Format a date and time the standard way.
 *
 * "short" pairs the short date with the time ("Jan 1 ・ 12:00 PM").
 * "long" spells it out as a phrase ("January 1, 2025 at 12:00 PM").
 */
export function formatStandardDatetimeString(
    date: string | Date | null | undefined,
    preferredTimezone?: string,
    format: StandardDateFormat = "short",
): string | null {
    const dateText = formatStandardDateString(date, preferredTimezone, format)

    if (!dateText) {
        return null
    }

    const timeText = dayjs(date).tz(preferredTimezone).format("h:mm A")

    return format === "long" ? `${dateText} at ${timeText}` : `${dateText} ${BULLET_CHARACTER} ${timeText}`
}

/**
 * Compose a tooltip that labels a moment in time, one segment per line
 * ("Sent\non January 1, 2025\nat 12:00 PM"). A future moment reads "for" instead of "on"
 * ("Scheduled\nfor January 1, 2025\nat 12:00 PM"). Without a date only the label is returned.
 */
export function formatDatetimeTooltip(
    label: string,
    date: string | Date | null | undefined,
    preferredTimezone?: string,
    isFuture = false,
): string {
    const datetimeText = formatStandardDatetimeString(date, preferredTimezone, "long")
    const preposition = isFuture ? "for" : "on"

    return datetimeText ? `${label}\n${preposition} ${datetimeText.replace(" at ", "\nat ")}` : label
}
